// LS Newsletter Popup
// Opens the theme's <dialog-component> on a delay, caps how often it reappears
// (localStorage), and hides itself from known subscribers.
//
// Submission uses the theme's NATIVE customer-form flow (same as email-signup /
// ls-newsletter): the form posts and Shopify redirects back with
// ?customer_posted=true. We flag the pending submit before it posts, then on the
// next load re-open the popup in its "You're in!" success state. This avoids
// fighting the native form handling with AJAX.

const DAY_MS = 24 * 60 * 60 * 1000;
const isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

/** @param {string} key @returns {boolean} */
function readFlag(key) {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch (e) {
    return false;
  }
}

/** @param {string} key */
function setFlag(key) {
  try {
    window.localStorage.setItem(key, '1');
  } catch (e) {
    /* storage unavailable — non-fatal */
  }
}

/**
 * Whether the popup is currently within its "don't show again" window.
 * @param {string} key
 * @param {number} frequencyDays
 * @returns {boolean}
 */
function isCapped(key, frequencyDays) {
  if (!frequencyDays || frequencyDays <= 0) return false;
  try {
    const last = Number(window.localStorage.getItem(key));
    if (!last) return false;
    return Date.now() - last < frequencyDays * DAY_MS;
  } catch (e) {
    return false;
  }
}

/** Record a dismissal timestamp so the frequency cap kicks in. @param {string} key */
function stampDismissed(key) {
  try {
    window.localStorage.setItem(key, String(Date.now()));
  } catch (e) {
    /* storage unavailable — non-fatal */
  }
}

/**
 * @param {HTMLElement & { showDialog?: () => void, closeDialog?: () => void }} el
 */
function initPopup(el) {
  if (el.dataset.nlpInit === 'true') return;
  el.dataset.nlpInit = 'true';

  const delayMs = Math.max(0, Number(el.dataset.delay || 0)) * 1000;
  const frequencyDays = Number(el.dataset.frequency || 0);
  const storageKey = el.dataset.storageKey || 'ls-nl-popup';
  const dismissedKey = `${storageKey}:dismissed`;
  const subscribedKey = `${storageKey}:subscribed`;
  const pendingKey = `${storageKey}:pending`;
  const hideSubscribed = el.dataset.hideSubscribed === 'true';
  const customerSubscribed = el.dataset.customerSubscribed === 'true';
  const guarded = el.hasAttribute('data-guard') && !isDesignMode;

  const formState = el.querySelector('[data-nlp-state="form"]');
  const successState = el.querySelector('[data-nlp-state="success"]');

  const open = () => {
    if (typeof el.showDialog === 'function') el.showDialog();
  };
  const isSubscribed = () => customerSubscribed || readFlag(subscribedKey);
  const showSuccess = () => {
    if (formState) formState.hidden = true;
    if (successState) successState.hidden = false;
  };

  // If we just came back from a native submit (Shopify redirected with
  // ?customer_posted=true) and WE initiated it, re-open in the success state.
  let handledSuccess = false;
  try {
    const url = new URL(window.location.href);
    const posted = url.searchParams.get('customer_posted') === 'true';
    if (posted && window.sessionStorage.getItem(pendingKey) === '1') {
      window.sessionStorage.removeItem(pendingKey);
      setFlag(subscribedKey);
      showSuccess();
      handledSuccess = true;

      // Clean the query param so a manual refresh doesn't re-trigger this.
      url.searchParams.delete('customer_posted');
      const hash = url.hash === '#ls-nl-popup-form' ? '' : url.hash;
      window.history.replaceState({}, document.title, url.pathname + url.search + hash);

      window.setTimeout(open, 150);
    }
  } catch (e) {
    /* URL/sessionStorage unavailable — non-fatal */
  }

  if (isDesignMode) {
    // In the editor: never auto-open; open only when this section is selected.
    document.addEventListener('shopify:section:select', (event) => {
      if (event.target instanceof HTMLElement && event.target.contains(el)) open();
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.target instanceof HTMLElement && event.target.contains(el) && typeof el.closeDialog === 'function') {
        el.closeDialog();
      }
    });
  } else if (!handledSuccess) {
    const suppressed = (hideSubscribed && isSubscribed()) || (guarded && isCapped(dismissedKey, frequencyDays));
    if (!suppressed) window.setTimeout(open, delayMs);
  }

  // Any close (X, backdrop, Escape) starts the non-subscriber "don't show
  // again" window. Subscribers are handled by the permanent subscribed flag.
  el.addEventListener('dialog:close', () => {
    if (!isDesignMode) stampDismissed(dismissedKey);
  });

  const form = el.querySelector('form');
  if (form) wireForm(el, form, pendingKey);
}

/**
 * Wire the native customer form. Validation mirrors the LS Contact block: the
 * browser stays the source of truth (a `required`/invalid field can never POST),
 * we only swap its native tooltip for the design-system inline error. On a valid
 * submit we DON'T intercept — we flag it as pending and let it post natively;
 * initPopup shows the success state after Shopify's redirect.
 * @param {HTMLElement} el
 * @param {HTMLFormElement} form
 * @param {string} pendingKey
 */
function wireForm(el, form, pendingKey) {
  const errorEl = el.querySelector('[data-nlp-error]');
  const emailInput = /** @type {HTMLInputElement|null} */ (form.querySelector('.ls-nl-popup__input'));

  const messageFor = (field) => {
    const v = field.validity;
    if (v.valueMissing) return el.dataset.requiredMessage || 'This field is required';
    if (v.typeMismatch && field.type === 'email') return el.dataset.invalidMessage || 'Please enter a valid email address';
    return el.dataset.requiredMessage || 'This field is required';
  };

  const showError = (field, message) => {
    field.classList.add('is-error');
    field.setAttribute('aria-invalid', 'true');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  };

  const clearError = (field) => {
    field.classList.remove('is-error');
    field.removeAttribute('aria-invalid');
    if (errorEl) errorEl.hidden = true;
  };

  let focusedThisPass = false;

  // `invalid` doesn't bubble — capture it at the form. The browser still blocks
  // the submit; we just replace its tooltip with our styled inline message.
  form.addEventListener(
    'invalid',
    (event) => {
      const field = event.target;
      if (!field.matches || !field.matches('.ls-nl-popup__input')) return;
      event.preventDefault();
      showError(field, messageFor(field));
      if (!focusedThisPass) {
        focusedThisPass = true;
        field.focus();
        requestAnimationFrame(() => {
          focusedThisPass = false;
        });
      }
    },
    true
  );

  // Clear the error as soon as the field becomes valid again.
  form.addEventListener('input', (event) => {
    const field = event.target.closest && event.target.closest('.ls-nl-popup__input');
    if (field && field.classList.contains('is-error') && field.checkValidity()) {
      clearError(field);
    }
  });

  // Only fires when the form is valid (the browser blocks it otherwise). Flag
  // the pending submit so the success state shows after the native redirect,
  // then let the form post natively (no preventDefault).
  form.addEventListener('submit', () => {
    try {
      window.sessionStorage.setItem(pendingKey, '1');
    } catch (e) {
      /* sessionStorage unavailable — non-fatal */
    }
  });
}

customElements.whenDefined('dialog-component').then(() => {
  document.querySelectorAll('.ls-nl-popup').forEach((el) => initPopup(/** @type {HTMLElement} */ (el)));
});
